/**
 * Multi-vault data layer. A `VaultDataProvider` (mounted once inside the
 * unlocked shell) loads and caches the user's vaults — decrypting each vault
 * NAME in tab memory — and exposes create/rename/delete. A companion
 * `useVaultItems(vaultId)` hook loads + mutates the items of a single vault.
 *
 * Zero-knowledge boundary is unchanged: the server sees only ciphertext. The
 * vault DEK encrypts ONLY the vault name (item DEKs are wrapped under the UMK,
 * not the vault DEK), so rotating it on rename touches no item ciphertext.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ItemPayload } from '@heivol-locker/shared-types';
import { ApiError, uuidv4 } from '@heivol-locker/vault-client';
import { useSession } from './session';
import {
  decryptItemRecord,
  decryptVaultName,
  encryptItemPayload,
  encryptVaultName,
} from '@heivol-locker/vault-crypto';
import type { ItemView } from '../components/items';

function msg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export interface VaultView {
  id: string;
  name: string;
  itemCount: number;
}

interface VaultDataValue {
  vaults: VaultView[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createVault: (name: string) => Promise<string>;
  renameVault: (id: string, name: string) => Promise<void>;
  deleteVault: (id: string) => Promise<void>;
}

const VaultDataContext = createContext<VaultDataValue | null>(null);

// Dedupe the first-run "Personal" vault bootstrap across React StrictMode's
// double-mounted effect (two refreshes would each observe zero vaults and create
// one). Module-level promise, reset on failure so it can retry.
let bootstrap: Promise<void> | null = null;

export function VaultDataProvider({ children }: { children: ReactNode }) {
  const client = useSession((s) => s.client);
  const session = useSession((s) => s.session);
  const [vaults, setVaults] = useState<VaultView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    if (session === null) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let recs = await client.listVaults();
      if (recs.length === 0) {
        if (bootstrap === null) {
          bootstrap = (async () => {
            const id = uuidv4();
            const enc = await encryptVaultName(session, id, 'Personal');
            await client.createVault({ id, nameBlob: enc.nameBlob, wrappedDek: enc.wrappedDek });
          })();
          bootstrap.catch(() => {
            bootstrap = null;
          });
        }
        await bootstrap;
        recs = await client.listVaults();
      }
      const views: VaultView[] = [];
      for (const v of recs) {
        views.push({ id: v.id, name: await decryptVaultName(session, v), itemCount: v.itemCount });
      }
      views.sort((a, b) => a.name.localeCompare(b.name));
      setVaults(views);
    } catch (e) {
      setError(msg(e));
    } finally {
      setLoading(false);
    }
  }, [client, session]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createVault = useCallback(
    async (name: string): Promise<string> => {
      if (session === null) {
        throw new Error('vault is locked');
      }
      const id = uuidv4();
      const enc = await encryptVaultName(session, id, name.trim() || 'Untitled');
      await client.createVault({ id, nameBlob: enc.nameBlob, wrappedDek: enc.wrappedDek });
      await refresh();
      return id;
    },
    [client, session, refresh],
  );

  const renameVault = useCallback(
    async (id: string, name: string): Promise<void> => {
      if (session === null) {
        throw new Error('vault is locked');
      }
      const enc = await encryptVaultName(session, id, name.trim() || 'Untitled');
      await client.updateVault(id, { nameBlob: enc.nameBlob, wrappedDek: enc.wrappedDek });
      await refresh();
    },
    [client, session, refresh],
  );

  const deleteVault = useCallback(
    async (id: string): Promise<void> => {
      await client.deleteVault(id);
      await refresh();
    },
    [client, refresh],
  );

  const value = useMemo<VaultDataValue>(
    () => ({ vaults, loading, error, refresh, createVault, renameVault, deleteVault }),
    [vaults, loading, error, refresh, createVault, renameVault, deleteVault],
  );

  return <VaultDataContext.Provider value={value}>{children}</VaultDataContext.Provider>;
}

export function useVaultData(): VaultDataValue {
  const ctx = useContext(VaultDataContext);
  if (ctx === null) {
    throw new Error('useVaultData must be used within a VaultDataProvider');
  }
  return ctx;
}

// ---- per-vault item loading + mutations ------------------------------------

export interface VaultItems {
  items: ItemView[];
  loading: boolean;
  error: string | null;
  notice: string | null;
  reload: () => Promise<void>;
  addItem: (payload: ItemPayload) => Promise<void>;
  saveItem: (id: string, version: number, payload: ItemPayload) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
}

export function useVaultItems(vaultId: string | undefined): VaultItems {
  const client = useSession((s) => s.client);
  const session = useSession((s) => s.session);
  const { refresh: refreshVaults } = useVaultData();
  const [items, setItems] = useState<ItemView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (session === null || vaultId === undefined) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await client.listItems({ vaultId });
      const views: ItemView[] = [];
      for (const it of list.items) {
        if (it.deletedAt !== null) {
          continue;
        }
        views.push({
          id: it.id,
          version: it.version,
          payload: await decryptItemRecord(session, it),
        });
      }
      setItems(views);
    } catch (e) {
      setError(msg(e));
    } finally {
      setLoading(false);
    }
  }, [client, session, vaultId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const addItem = useCallback(
    async (payload: ItemPayload): Promise<void> => {
      if (session === null || vaultId === undefined) {
        return;
      }
      setError(null);
      const itemId = uuidv4();
      const enc = await encryptItemPayload(session, vaultId, itemId, payload);
      try {
        await client.createItem({
          id: itemId,
          vaultId,
          kind: payload.kind,
          domainHash: enc.domainHash,
          wrappedDek: enc.wrappedDek,
          ciphertext: enc.ciphertext,
        });
        await reload();
        await refreshVaults();
      } catch (e) {
        setError(msg(e));
        throw e;
      }
    },
    [client, session, vaultId, reload, refreshVaults],
  );

  const saveItem = useCallback(
    async (id: string, version: number, payload: ItemPayload): Promise<void> => {
      if (session === null || vaultId === undefined) {
        return;
      }
      setError(null);
      setNotice(null);
      const enc = await encryptItemPayload(session, vaultId, id, payload);
      try {
        await client.updateItem(id, {
          expectedVersion: version,
          kind: payload.kind,
          domainHash: enc.domainHash,
          wrappedDek: enc.wrappedDek,
          ciphertext: enc.ciphertext,
        });
        await reload();
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          setNotice('This item changed on another device — reloaded the latest version.');
          await reload();
          return;
        }
        setError(msg(e));
        throw e;
      }
    },
    [client, session, vaultId, reload],
  );

  const deleteItem = useCallback(
    async (id: string): Promise<void> => {
      setError(null);
      try {
        await client.deleteItem(id);
        await reload();
        await refreshVaults();
      } catch (e) {
        setError(msg(e));
        throw e;
      }
    },
    [client, reload, refreshVaults],
  );

  return { items, loading, error, notice, reload, addItem, saveItem, deleteItem };
}
